import { useCallback, useEffect, useRef, useState } from 'react';

// DrawingPanel Component (defined locally)
const DrawingPanel = ({ currentColor, setCurrentColor, lineWidth, handleLineWidthChange, clearCanvas }) => {
    // ... (rest of DrawingPanel remains unchanged)
    const handleColorChange = (e) => {
        setCurrentColor(e.target.value);
    }
    return (
        <div className="draw-menu-content">
            <div className="draw-menu-content-bar">

                <p className="sizeLabel">Color:</p>
                <div>

                    <input
                        type="color"
                        value={currentColor}
                        onChange={handleColorChange}
                        className="color-input"
                        aria-label="Brush Color"
                    />
                </div>
                <p className="sizeLabel">Size:</p>
                <button
                    onClick={() => handleLineWidthChange(-1)}
                    className="size-button decrease"
                    aria-label="Decrease brush size"
                >
                    <span className="size-icon">-</span>
                </button>
                <p className="line-width-display">{lineWidth}</p>
                <button
                    onClick={() => handleLineWidthChange(1)}
                    className="size-button increase"
                    aria-label="Increase brush size"
                >
                    <span className="size-icon">+</span>
                </button>

                <i className="fa fa-eraser clear-button"
                    title='Clear Canvas'
                    onClick={clearCanvas}
                    onTouchEnd={clearCanvas}
                >

                </i>
            </div>

        </div>
    );
}

const Drawing = ({ drawMode, setCustomCallback }) => {
    const canvasRef = useRef(null);
    const ctxRef = useRef(null);
    const [isDrawing, setIsDrawing] = useState(false);
    const [currentColor, setCurrentColor] = useState('#000000');
    const [lineWidth, setLineWidth] = useState(5);
    const lastPosRef = useRef({ x: 0, y: 0 });

    // --- 1. Canvas Context Initialization (Runs ONCE on mount) ---
    // This effect initializes the canvas context and should NOT re-run when drawMode changes.
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        if (!ctx) return;

        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';

        // Initial setup for context ref
        ctxRef.current = ctx;

        // Initial sizing (will be refined by the resize effect)
        const container = canvas.parentElement.parentElement;
        if (container) {
            canvas.width = container.clientWidth;
            canvas.height = container.clientHeight;
        }

    }, []); // <-- EMPTY Dependency Array

    // --- 2. Resize/Redraw Effect (Runs on drawMode change and window resize) ---
    // This effect manages the canvas dimensions and saves/restores drawing data
    // whenever the container size *might* change (i.e., when drawMode toggles).
    useEffect(() => {
        const canvas = canvasRef.current;
        const ctx = ctxRef.current;
        if (!canvas || !ctx) return;

        const handleResize = () => {
            const canvas = canvasRef.current;
            const ctx = ctxRef.current;

            // We no longer need controlsRef since we won't subtract its height.
            if (!canvas || !ctx) return;

            const oldImageData = ctx.getImageData(0, 0, canvas.width, canvas.height);

            const newContainer = canvas.parentElement.parentElement;
            if (!newContainer) return;

            // --- FIX: Stop subtracting control bar height ---
            // Since .drawing-controls-bar is position: fixed, 
            // it does NOT occupy space in the vertical layout flow of the canvas.
            const scrollHandleWidth = 25;

            // The new height is simply the container's full height.
            // The control bar height subtraction logic is REMOVED.
            canvas.width = newContainer.clientWidth - scrollHandleWidth;
            canvas.height = newContainer.clientHeight; // <-- No subtraction needed!

            // Re-apply and restore drawing
            ctx.lineCap = 'round';
            ctx.lineJoin = 'round';
            ctx.strokeStyle = currentColor;
            ctx.lineWidth = lineWidth;
            ctx.putImageData(oldImageData, 0, 0);
        };

        window.addEventListener('resize', handleResize);
        handleResize(); // <-- Run initial sizing when drawMode changes

        return () => window.removeEventListener('resize', handleResize);

    }, [drawMode, currentColor, lineWidth]); // <-- Now depends on drawMode, color, and size

    // --- Update context properties when color or line width changes (Separate effect) ---
    useEffect(() => {
        const ctx = ctxRef.current;
        if (ctx) {
            ctx.strokeStyle = currentColor;
            ctx.lineWidth = lineWidth;
        }
    }, [currentColor, lineWidth]);


    // --- Event Handlers (Drawing Logic) ---

    // Get mouse coordinates relative to the canvas
    const getMouseCoords = (event) => {
        const canvas = canvasRef.current;
        if (!canvas) return { x: 0, y: 0 };
        const rect = canvas.getBoundingClientRect();
        const isTouch = event.touches && event.touches.length > 0;
        const clientX = isTouch ? event.touches[0].clientX : event.clientX;
        const clientY = isTouch ? event.touches[0].clientY : event.clientY;

        // Apply scaling based on the ratio of the canvas's internal width/height to its CSS size
        const scaleX = canvas.width / rect.width;
        const scaleY = canvas.height / rect.height;

        return {
            x: (clientX - rect.left) * scaleX,
            y: (clientY - rect.top) * scaleY,
        };
    };

    const startDrawing = useCallback((event) => {
        const isTouch = event.touches && event.touches.length > 0;
        const targetElement = isTouch ? event.touches[0].target : event.target;

        // If the tap/click starts on the scroll-handle-area, allow default scrolling behavior
        if (targetElement.classList.contains('scroll-handle-area')) {
            return;
        }

        // Crucial for mobile: Prevent default touch scrolling/panning immediately ONLY if on the canvas
        if (isTouch) {
            event.preventDefault();
        }

        if (event.button === 0 || isTouch) {
            const { x, y } = getMouseCoords(event);
            const ctx = ctxRef.current;
            if (ctx) {
                ctx.beginPath();
                ctx.moveTo(x, y); // Set the start of the path
                lastPosRef.current = { x, y }; // Store the initial position

                setIsDrawing(true);
            }
        }
    }, []);

    // Draw
    const draw = useCallback((event) => {
        // Crucial for mobile: Prevent default touch scrolling/panning immediately
        // Only prevent default if we are currently drawing
        if (isDrawing && event.touches) {
            event.preventDefault();
        }

        if (!isDrawing) return;

        const { x, y } = getMouseCoords(event);
        const ctx = ctxRef.current;
        if (ctx) {
            ctx.moveTo(lastPosRef.current.x, lastPosRef.current.y);
            ctx.lineTo(x, y);
            ctx.stroke();
            lastPosRef.current = { x, y };
        }
    }, [isDrawing]);

    // Stop Drawing
    const stopDrawing = useCallback(() => {
        const ctx = ctxRef.current;
        if (ctx) {
            ctx.closePath();
        }
        setIsDrawing(false);
    }, []);

    // Clear Canvas
    const clearCanvas = useCallback(() => {
        const canvas = canvasRef.current;
        const ctx = ctxRef.current;
        if (canvas && ctx) {
            ctx.clearRect(0, 0, canvas.width, canvas.height);
        }
    }, []);

    useEffect(() => {
        setCustomCallback(() => clearCanvas);
    }, [clearCanvas, setCustomCallback]);

    // --- UI Handlers ---
    const handleLineWidthChange = (change) => {
        setLineWidth(prev => Math.max(1, Math.min(50, prev + change)));
    };

    return (
        <div className={`canvas-container ${!drawMode ? 'pass-through' : ''}`}>
            {/* 1. Main Content Area (Scroll Handle + Canvas) - Renders at the TOP */}
            <div className="drawing-content-area">
                {/* 1b. Scroll Handle Area (Right space) */}
                <div className="scroll-handle-area"
                    // CRITICAL: Explicitly allow touch actions for scrolling
                    style={{ touchAction: 'pan-y' }}
                >
                </div>
                {/* 1a. Canvas (Left and center space) */}
                <canvas
                    ref={canvasRef}
                    // Conditionally attach mouse handlers
                    onMouseDown={drawMode ? startDrawing : undefined}
                    onMouseMove={drawMode ? draw : undefined}
                    onMouseUp={drawMode ? stopDrawing : undefined}
                    onMouseLeave={drawMode ? stopDrawing : undefined}
                    // Conditionally attach touch handlers
                    onTouchStart={drawMode ? startDrawing : undefined}
                    onTouchMove={drawMode ? draw : undefined}
                    onTouchEnd={drawMode ? stopDrawing : undefined}
                    className="drawing-canvas"
                ></canvas>

            </div>

            {/* 2. Drawing Controls Bar - Renders at the BOTTOM */}
            <div className="drawing-controls-bar">
                {drawMode && ( // Only render the controls if draw mode is active
                    <DrawingPanel
                        currentColor={currentColor}
                        setCurrentColor={setCurrentColor}
                        lineWidth={lineWidth}
                        handleLineWidthChange={handleLineWidthChange}
                        clearCanvas={clearCanvas}
                    />
                )}
            </div>
        </div>
    );
}
export default Drawing;